import { useEffect, useRef, useState } from 'react';
import {
  ClerkProvider,
  SignedIn,
  SignedOut,
  SignIn,
  SignUp,
  useAuth,
} from '@clerk/clerk-react';
import { RouterProvider, useRouter, match } from './router';
import { api, ApiError, DEV_EMAIL_KEY, setAuthTokenGetter } from './api';
import type { User } from '../shared/types';

import { BoardsList } from './screens/BoardsList';
import { BoardView } from './screens/BoardView';
import { ShareCompose } from './screens/ShareCompose';
import { ProfileScreen } from './screens/Profile';
import { TenantScreen } from './screens/Tenant';
import { Welcome } from './screens/Welcome';
import { DevIdentity } from './screens/DevIdentity';
import { Layout } from './components/Layout';
import { PublicDeck } from './screens/PublicDeck';
import { applyTheme, startSystemThemeWatcher } from './lib/themes';
import { ToastProvider } from './components/Toast';
import { CalendarScreen } from './screens/Calendar';
import { ProposalWatcher } from './components/ProposalWatcher';

const CLERK_PUBLISHABLE_KEY = import.meta.env.VITE_CLERK_PUBLISHABLE_KEY as
  | string
  | undefined;

/**
 * Three modes for the auth shell:
 *
 *   1. CLERK_PUBLISHABLE_KEY set      → wrap in <ClerkProvider>, real auth.
 *   2. dev (no key) + bypass enabled  → <DevIdentity> picker, headers sent
 *      to a worker running DEV_AUTH_BYPASS=1. Lets you build the UI offline.
 *   3. no key + prod                  → fail loud, ask for configuration.
 */
export default function App() {
  // Public deck route bypasses Clerk entirely — anyone with the token
  // can view, no sign-in. Has to be matched BEFORE we mount ClerkProvider
  // because we don't want sign-in walls on a shared public link.
  const path = typeof window !== 'undefined' ? window.location.pathname : '/';
  const deckMatch = match('/d/:token', path);
  if (deckMatch?.token) {
    return (
      <RouterProvider>
        <PublicDeck token={deckMatch.token} />
      </RouterProvider>
    );
  }

  if (CLERK_PUBLISHABLE_KEY) {
    return (
      <ClerkProvider
        publishableKey={CLERK_PUBLISHABLE_KEY}
        appearance={{ variables: { colorPrimary: '#9a4d3a' } }}
      >
        <RouterProvider>
          <ClerkAuthShell />
        </RouterProvider>
      </ClerkProvider>
    );
  }
  // import.meta.env.DEV covers `vite serve` (dev). VITE_DEV_AUTH=1 is
  // an explicit opt-in for built bundles that still want the x-dev-email
  // picker — used by Playwright E2E, never set for the real deploy.
  if (import.meta.env.DEV || import.meta.env.VITE_DEV_AUTH === '1') {
    return (
      <RouterProvider>
        <DevAuthShell />
      </RouterProvider>
    );
  }
  return (
    <Welcome reason="Clerk publishable key not configured. Set VITE_CLERK_PUBLISHABLE_KEY at build time." />
  );
}

/**
 * Authenticated path with Clerk: gate the app behind <SignedIn>, render
 * Clerk's hosted sign-in for <SignedOut>. The bearer-token getter is
 * registered exactly once so api.ts can call it for every request.
 */
function ClerkAuthShell() {
  const { getToken, isLoaded, isSignedIn } = useAuth();
  const { pathname } = useRouter();

  useEffect(() => {
    setAuthTokenGetter(() => getToken());
  }, [getToken]);

  // Sign-out detection: clear the PostHog identity + workspace group
  // the moment Clerk reports signed-out. Without this, the next person
  // on the same browser (kiosk / shared laptop) inherits the previous
  // user's distinct_id until /api/me runs again with a fresh identity.
  // We only fire on the true → false transition; the initial isLoaded
  // pass where everything is still null doesn't count.
  const prevSignedInRef = useRef<boolean | null>(null);
  useEffect(() => {
    if (!isLoaded) return;
    const prev = prevSignedInRef.current;
    prevSignedInRef.current = isSignedIn ?? false;
    if (prev === true && isSignedIn === false) {
      void import('./lib/analytics').then(({ resetAnalytics }) => resetAnalytics());
    }
  }, [isLoaded, isSignedIn]);

  if (!isLoaded) return <SplashShimmer />;

  // Invitation landing. Clerk appends __clerk_ticket to the redirectUrl;
  // <SignUp> reads it from the URL and lets the recipient complete
  // sign-up even though the org is closed to public registration. If
  // they're already signed in (e.g. they clicked the email on the same
  // device where they already have an account), fall through and let
  // the board membership conversion in /api/me do its job.
  if (pathname === '/accept-invite' && !isSignedIn) {
    return <AcceptInvitePanel />;
  }

  return (
    <>
      <SignedOut>
        <SignInPanel />
      </SignedOut>
      <SignedIn>
        <Bootstrapped key={isSignedIn ? 'in' : 'out'} />
      </SignedIn>
    </>
  );
}

function AcceptInvitePanel() {
  // Recover the board we should land on after sign-up. The worker
  // encodes ?board=<id> in the invitation's redirectUrl; we forward it
  // as Clerk's post-signup destination so the recipient ends up on the
  // board they were invited to, not the empty dashboard.
  const params =
    typeof window !== 'undefined' ? new URLSearchParams(window.location.search) : null;
  const boardId = params?.get('board') ?? null;
  const afterUrl = boardId ? `/boards/${boardId}` : '/';
  return (
    <div className="flex min-h-screen items-center justify-center bg-ink p-6">
      <div className="flex flex-col items-center gap-6">
        <Logo size={56} animate />
        <h1 className="text-2xl">Welcome to SynFolio</h1>
        <p className="-mt-2 max-w-xs text-center text-sm text-cream-3">
          Finish setting up your account to open the board.
        </p>
        <SignUp
          appearance={{ variables: { colorPrimary: '#9a4d3a' } }}
          routing="hash"
          signInUrl="/"
          forceRedirectUrl={afterUrl}
        />
      </div>
    </div>
  );
}

/**
 * Offline-dev path: legacy x-dev-email picker. Kept so the UI is
 * exercisable without an internet round-trip to Clerk.
 */
function DevAuthShell() {
  const [picked, setPicked] = useState(
    () => typeof localStorage !== 'undefined' && !!localStorage.getItem(DEV_EMAIL_KEY),
  );
  if (!picked) {
    return (
      <DevIdentity
        onSelect={(email) => {
          localStorage.setItem(DEV_EMAIL_KEY, email);
          setPicked(true);
        }}
      />
    );
  }
  return <Bootstrapped />;
}

function SignInPanel() {
  return (
    <div className="flex min-h-screen items-center justify-center bg-ink p-6">
      <div className="flex flex-col items-center gap-6">
        <Logo size={56} animate />
        <h1 className="text-2xl">SynFolio</h1>
        <p className="-mt-2 max-w-xs text-center text-sm text-cream-3">
          Sign in to continue.
        </p>
        {/* No signUpUrl — SynFolio is invitation-only. New users arrive
            via /accept-invite with a Clerk ticket; the SignIn widget
            shouldn't dangle a sign-up link that goes nowhere. */}
        <SignIn
          appearance={{ variables: { colorPrimary: '#9a4d3a' } }}
          routing="hash"
        />
      </div>
    </div>
  );
}

/**
 * Load /api/me — the worker resolves the Clerk token → user → tenant.
 * A 401/403 surfaces the Welcome screen so the user can re-authenticate.
 */
function Bootstrapped() {
  const [state, setState] = useState<
    | { kind: 'loading' }
    | { kind: 'auth-required'; reason: string }
    | {
        kind: 'ready';
        user: User;
        tenant: { id: string; slug: string; name: string } | null;
        workspaces: Array<{
          id: string;
          slug: string;
          name: string;
          isPersonal: boolean;
          role: 'admin' | 'member';
        }>;
      }
  >({ kind: 'loading' });

  useEffect(() => {
    let cancelled = false;
    void api
      .me()
      .then(async (r) => {
        if (cancelled) return;
        // Tell PostHog who this is. Name is included so the People
        // view shows something human-readable; we deliberately
        // OMIT email — PostHog's project key is in the browser
        // bundle and Resend/Clerk already see the email address,
        // no reason to send it to a third place. Workspace info
        // goes into a `workspace` group so events can be filtered
        // by org / family / team in cohorts.
        try {
          const { identifyUser, setWorkspaceGroup } = await import('./lib/analytics');
          identifyUser(r.user.id, {
            name: r.user.name,
            tenant_id: r.tenant?.id ?? null,
            tenant_role: r.user.tenantRole,
          });
          if (r.tenant) {
            // workspaces[] also has isPersonal; find the active one.
            const active = (r.workspaces ?? []).find((w) => w.id === r.tenant!.id);
            setWorkspaceGroup(r.tenant.id, {
              slug: r.tenant.slug,
              name: r.tenant.name,
              isPersonal: active?.isPersonal,
            });
          }
        } catch {
          /* analytics off — fine */
        }
        setState({
          kind: 'ready',
          user: r.user,
          tenant: r.tenant,
          workspaces: r.workspaces ?? [],
        });
      })
      .catch((e: unknown) => {
        if (cancelled) return;
        if (e instanceof ApiError && (e.status === 401 || e.status === 403)) {
          setState({ kind: 'auth-required', reason: e.message });
        } else {
          setState({ kind: 'auth-required', reason: 'Could not load your profile.' });
        }
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // When the user's preference is 'system', keep the theme in sync with
  // macOS / Windows dark-light flips without requiring a page reload.
  const userPref =
    state.kind === 'ready' ? state.user.themePreference ?? null : null;
  useEffect(() => {
    if (userPref !== 'system') return;
    const stop = startSystemThemeWatcher(() => {
      applyTheme({ userPreference: 'system' });
    });
    return () => stop();
  }, [userPref]);

  if (state.kind === 'loading') return <SplashShimmer />;
  if (state.kind === 'auth-required') return <Welcome reason={state.reason} />;

  return (
    <ToastProvider>
      <Layout user={state.user} tenant={state.tenant} workspaces={state.workspaces}>
        <Routes user={state.user} />
      </Layout>
      {/* Polls for tasks the scheduler proposed and surfaces a toast.
          Lives at the shell so a proposal can land on any screen. */}
      <ProposalWatcher />
    </ToastProvider>
  );
}

function Routes({ user }: { user: User }) {
  const { pathname } = useRouter();
  const [me, setMe] = useState(user);
  // Keep local profile in sync if the parent's user prop changes.
  useEffect(() => setMe(user), [user]);

  if (pathname === '/') return <BoardsList user={me} />;
  if (pathname === '/share/compose') return <ShareCompose user={me} />;
  if (pathname === '/profile') return <ProfileScreen user={me} onUserChange={setMe} />;
  if (pathname === '/tenant') return <TenantScreen user={me} />;
  if (pathname === '/calendar') return <CalendarScreen />;

  const m = match('/boards/:id', pathname);
  if (m?.id) return <BoardView boardId={m.id} user={me} />;

  return <NotFound />;
}

function SplashShimmer() {
  return (
    <div className="flex h-full items-center justify-center">
      <div className="relative">
        {/* Slowly rotating conic halo — sits BEHIND the aura. */}
        <span aria-hidden className="splash-halo" />
        {/* Existing soft pulse aura. */}
        <span
          aria-hidden
          className="splash-aura pointer-events-none absolute inset-0 -m-12 rounded-full"
          style={{
            background:
              'radial-gradient(closest-side, var(--color-clove) 0%, transparent 70%)',
            filter: 'blur(28px)',
          }}
        />
        <div className="splash-logo relative">
          <Logo size={140} animate />
        </div>
      </div>
    </div>
  );
}

function NotFound() {
  return (
    <div className="flex h-full flex-col items-center justify-center gap-3 text-cream-2">
      <Logo size={42} />
      <p className="text-sm">Nothing here.</p>
      <a href="/" className="btn btn-ghost">
        Back home
      </a>
    </div>
  );
}

/**
 * SynFolio mark — two overlapping rounded "page" cards on a clove
 * plate. The pages are the folio; the overlap is the synergy. Single
 * confident silhouette, scales from favicon to splash, reads either
 * way against ink or cream backgrounds because the card fills are
 * explicit (not currentColor).
 */
export function Logo({
  size = 24,
  animate = false,
}: {
  size?: number;
  /** When true, run the one-shot bounce-in animation on mount.
   *  Off by default so navigations don't re-trigger it. */
  animate?: boolean;
}) {
  // Per-instance gradient id so multiple Logos on a page don't clash.
  const gid = `synfolio-plate-${size}`;
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 32 32"
      fill="none"
      aria-label="SynFolio"
      role="img"
      className={animate ? 'logo-in' : undefined}
    >
      <defs>
        <linearGradient id={gid} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="var(--color-clove-2)" />
          <stop offset="100%" stopColor="var(--color-clove)" />
        </linearGradient>
      </defs>
      {/* Square plate. Slightly larger radius + dark rim + vertical
          highlight gradient = the mark has weight at every size. */}
      <rect
        x="1.5"
        y="1.5"
        width="29"
        height="29"
        rx="8"
        fill={`url(#${gid})`}
        stroke="color-mix(in oklab, var(--color-clove) 60%, black 40%)"
        strokeWidth="0.75"
      />
      {/* Back card — muted cream, upper-left. Sits behind. */}
      <rect
        x="5"
        y="5"
        width="14"
        height="18"
        rx="2"
        fill="var(--color-cream-2)"
      />
      {/* Front card — full cream, lower-right. Overlaps the back card
          by ~50% on x and most of the height — enough to read as one
          compound silhouette while still showing both as distinct
          pages. */}
      <rect
        x="12"
        y="8"
        width="14"
        height="18"
        rx="2"
        fill="var(--color-cream)"
      />
    </svg>
  );
}
